import { ElMessage } from 'element-plus';
import { defineStore } from 'pinia';
import { client, getErrorMessage } from '@/api';
import { CollectionGameDTO, ModificationType } from '@/api/models';
import {
  GetGameRecommendationsRequest,
  ZingBrainAvailableCountries,
  ZingBrainFilters,
  ZingBrainFiltersItemRequest,
  ZingBrainFiltersProvider,
} from '@/api/response/zing-brain/zing-brain.types';
import { CollectionPresetResponse } from '@/api/response/collections/PresetCollection';
import { watch, WatchHandle } from 'vue';
import dayjs from 'dayjs';
import { useAggregators } from '@/store';

export type EditingGame = CollectionGameDTO & {
  modificationType?: ModificationType;
  isHidden?: boolean;
};

export const MIN_COLLECTION_SIZE = 20;
export const DEF_COLLECTION_SIZE = 20;
export const MAX_COLLECTION_SIZE = 1000;

export const ZING_BRAIN_ID = '1' as const;

export function validateCollectionSize(size: number): boolean {
  if (size < MIN_COLLECTION_SIZE || size > MAX_COLLECTION_SIZE) {
    ElMessage.error(
      `Collection size must be between ${MIN_COLLECTION_SIZE} and ${MAX_COLLECTION_SIZE}. Current size: ${size}`,
    );
    return false;
  }
  return true;
}

const gameNameParams = [
  ['Features', 'gameFeatures'],
  ['Themes', 'gameThemes'],
];

export type ZingBrainStoreState = {
  loading: boolean;
  filtersLoading: boolean;
  presetLoading: boolean;
  availableCountriesLoading: boolean;
  error: string | null;
  collectionSourceId: string;
  editingGames: EditingGame[];
  filters: ZingBrainFilters;
  presets: CollectionPresetResponse[];
  presetId: string;
  presetSearch: string;
  presetTotal: number;
  localeId: number | null;
  gameTypes: string[];
  selectedGameTypes: string[];
  providers: ZingBrainFiltersProvider[];
  providerId: string;
  releaseDate: Date | null;
  collectionSize: number;
  gameParameters: string[];
  selectedGameParameters: string[];
  gameParametersValue: Record<string, string[]>;
  gameRecommendationsRequest: GetGameRecommendationsRequest;
  availableCountries: ZingBrainAvailableCountries;
  country: string;
};

const getInitState = () => ({
  loading: false,
  filtersLoading: false,
  presetLoading: false,
  availableCountriesLoading: false,
  error: null,
  // HARDCODE!!! F..k :'-(
  collectionSourceId: ZING_BRAIN_ID,
  editingGames: [],
  filters: {},
  presets: [],
  presetId: '',
  presetSearch: '',
  presetTotal: 0,
  localeId: null,
  gameTypes: [],
  selectedGameTypes: [],
  providers: [],
  providerId: '',
  releaseDate: null,
  collectionSize: DEF_COLLECTION_SIZE,
  gameParameters: [],
  selectedGameParameters: [],
  gameParametersValue: {},
  gameRecommendationsRequest: {} as GetGameRecommendationsRequest,
  availableCountries: {
    segmentType: 'country',
    values: [],
  },
  country: '',
});

export const useZingBrainStore = defineStore('zingBrainStore', {
  state: (): ZingBrainStoreState => getInitState(),

  actions: {
    setLoading(value: boolean): void {
      this.loading = value;
    },

    setFiltersLoading(value: boolean): void {
      this.filtersLoading = value;
    },

    setPresetLoading(value: boolean): void {
      this.presetLoading = value;
    },

    setAvailableCountriesLoading(value: boolean): void {
      this.availableCountriesLoading = value;
    },

    setError(value: string | null): void {
      this.error = value;
    },

    async init(): Promise<void> {
      await Promise.all([
        this.getAvailableCountries(),
        this.getGameCollectionsFilters(),
        this.listCollectionPresets(),
      ]);
    },

    async reset(): Promise<void> {
      this.$reset();

      await this.init();
    },

    setPresetSearch(value: string): void {
      this.presetSearch = value;

      this.listCollectionPresets();
    },

    setPresetId(value: string): void {
      this.presetId = value;

      const preset = this.presets.find((p) => p.id === value);

      if (preset?.parameters) {
        this.initFromParams(preset.parameters);
      }
    },

    setLocaleId(value: number | null): void {
      this.localeId = value ?? null;
    },

    setSelectedGameTypes(value: string[]): void {
      this.selectedGameTypes = value;
    },

    setProviderId(value: string): void {
      this.providerId = value;
    },

    setReleaseDate(value: Date | null): void {
      this.releaseDate = value;
    },

    setCollectionSize(value: number): void {
      this.collectionSize = value;
    },

    getGameParametersValuesByKey(key: string): string[] {
      for (const [name, propKey] of gameNameParams) {
        if (name === key) {
          return (this.filters as any)[propKey] ?? [];
        }
      }

      return [];
    },

    setSelectedGameParameter(value: string, defValues: string[] = []): void {
      if (!this.gameParametersValue[value]) {
        const items: string[] = [];

        if (defValues.length > 0) {
          const availableValue = this.getGameParametersValuesByKey(value);

          for (const v of defValues) {
            if (availableValue.includes(v)) {
              items.push(v);
            }
          }
        }

        this.gameParametersValue[value] = items;
      }
    },

    setSelectedGameParameters(value: string[]): void {
      this.selectedGameParameters = value ?? [];

      for (const item of this.selectedGameParameters) {
        this.setSelectedGameParameter(item);
      }

      for (const name of Object.keys(this.gameParametersValue)) {
        if (!this.selectedGameParameters.includes(name)) {
          delete this.gameParametersValue[name];
        }
      }
    },

    setGameParametersValue(name: string, value: string[] = []): void {
      this.gameParametersValue = {
        ...this.gameParametersValue,
        [name]: value,
      };
    },

    setCountry(value: string): void {
      this.country = value;
    },

    initFromParams(params: Record<string, any>): void {
      this.country = '';
      this.selectedGameTypes = [];
      this.providerId = '';
      this.releaseDate = null;
      this.selectedGameParameters = [];
      this.gameParametersValue = {};

      this.collectionSize = params?.pagination?.limit ?? DEF_COLLECTION_SIZE;

      if (params?.country && this.availableCountries.values.includes(params.country)) {
        this.country = params.country;
      }

      const filters = params?.filters ?? {};
      if (Array.isArray(filters?.gameTypes?.values) && filters.gameTypes.values.length > 0) {
        const values = [];

        for (const gt of filters.gameTypes.values) {
          if (this.gameTypes.includes(gt)) {
            values.push(gt);
          }
        }

        this.selectedGameTypes = values;
      }

      if (
        Array.isArray(filters?.gameProviderIds?.values) &&
        filters.gameProviderIds.values.length > 0
      ) {
        const values = [];

        for (const providerId of filters.gameProviderIds.values) {
          if (this.providers.some((i) => i.gameProviderId === providerId)) {
            values.push(providerId);
          }
        }

        if (values.length > 0) {
          this.providerId = values[0];
        }
      }

      if (filters?.minReleaseDate) {
        this.releaseDate = dayjs(filters.minReleaseDate).toDate();
      }

      const selectedGameParameters: string[] = [];

      for (const [name, propKey] of gameNameParams) {
        if (Array.isArray(filters?.[propKey]?.values) && filters[propKey].values.length > 0) {
          const values: string[] = [];
          const items = this.getGameParametersValuesByKey(name);

          for (const value of filters[propKey].values) {
            if (items.includes(value)) {
              values.push(value);
            }
          }

          selectedGameParameters.push(name);
          this.setSelectedGameParameter(name, values);
        }
      }

      this.selectedGameParameters = selectedGameParameters;

      this.genGameRecommendationsRequest();
    },

    watchingChanges(): WatchHandle {
      return watch(
        () => [
          this.localeId,
          this.selectedGameTypes,
          this.providerId,
          this.releaseDate,
          this.collectionSize,
          this.selectedGameParameters,
          this.gameParametersValue,
          this.country,
        ],
        () => this.genGameRecommendationsRequest(),
        { deep: true },
      );
    },

    actualizeProviders(): void {
      // Do not block main loop and check it in background
      setTimeout(() => {
        const aggregatorsStore = useAggregators();

        this.providers = this.providers.filter((p) =>
          aggregatorsStore.providers.has(p.gameProviderName),
        );
      }, 50);
    },

    genGameRecommendationsRequest(): void {
      let gameFeatures: ZingBrainFiltersItemRequest | undefined;
      let gameThemes: ZingBrainFiltersItemRequest | undefined;
      let gameTypes: ZingBrainFiltersItemRequest | undefined;
      let gameProviderIds: ZingBrainFiltersItemRequest | undefined;
      let minReleaseDate: string | undefined;
      let maxReleaseDate: string | undefined;

      if (this.gameParametersValue.Features && this.gameParametersValue.Features.length > 0) {
        gameFeatures = {
          values: this.gameParametersValue.Features,
          op: 'anyOf',
        };
      }

      if (this.gameParametersValue.Themes && this.gameParametersValue.Themes.length > 0) {
        gameThemes = {
          values: this.gameParametersValue.Themes,
          op: 'anyOf',
        };
      }

      if (this.selectedGameTypes.length > 0) {
        gameTypes = {
          values: this.selectedGameTypes,
          op: 'anyOf',
        };
      }

      if (this.providerId) {
        gameProviderIds = {
          values: [this.providerId],
          op: 'anyOf',
        };
      }

      if (this.releaseDate !== null) {
        minReleaseDate = dayjs(this.releaseDate).format('YYYY-MM-DD');
        maxReleaseDate = dayjs().format('YYYY-MM-DD');
      }

      this.gameRecommendationsRequest = {
        filters: {
          gameTypes,
          gameFeatures,
          gameThemes,
          gameProviderIds,
          minReleaseDate,
          maxReleaseDate,
        },
        segmentType: this.availableCountries.segmentType,
        country: this.country,
        pagination: {
          page: 1,
          limit: this.collectionSize,
        },
      };
    },

    async getRecommendations(
      collectionId: string,
      params?: Record<string, unknown>,
    ): Promise<EditingGame[]> {
      this.setError(null);
      this.setLoading(true);

      this.editingGames = [];

      try {
        const response = await client.collectionsApi.getGamesRecommendations(
          params ?? this.gameRecommendationsRequest,
        );

        if ('error' in response) {
          throw new Error(response.error_description);
        }

        const recommendations = response.values || [];

        const requestedSlugs = recommendations.map((g) => g.gameId);
        const { slugs: validSlugs } = requestedSlugs.length > 0
          ? await client.collectionsApi.resolveGames(requestedSlugs) :
          { slugs: [] };

        const validSet = new Set(validSlugs);
        const filteredOutCount = recommendations.length - validSlugs.length;
        if (filteredOutCount > 0) {
          ElMessage.info(
            `Filtered out ${filteredOutCount} games. To get the requested amount, try requesting more games from the integration.`,
          );
        }

        this.editingGames = recommendations
          .filter((rec) => validSet.has(rec.gameId))
          .map((game, index) => ({
            collectionId: collectionId || '',
            gameSlug: game.gameId,
            gameNameEn: '',
            aggregatorName: '',
            providerName: '',
            gameImageUrl: '',
            rtp: 0,
            position: index + 1,
            isManual: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            modificationType: 'INSERT' as ModificationType,
          }));

        if (this.editingGames.length === 0) {
          ElMessage.info('No games found for the provided parameters');
        } else {
          ElMessage.success(`Loaded ${this.editingGames.length} games`);
        }
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting games recommendations');

        this.setError(message);

        throw err;
      } finally {
        this.setLoading(false);
      }

      return this.editingGames;
    },

    async getGameCollectionsFilters(): Promise<ZingBrainFilters> {
      this.setError(null);
      this.setFiltersLoading(true);

      this.filters = {};
      this.selectedGameTypes = [];
      this.gameParameters = [];
      this.selectedGameParameters = [];
      this.providers = [];

      try {
        this.filters = await client.collectionsApi.getGameCollectionsFilters();

        this.gameTypes = this.filters.gameTypes ?? [];
        this.providers = this.filters.gameProviders ?? [];

        const self = this as any;

        for (const [name, propKey] of gameNameParams) {
          if (Array.isArray(self.filters[propKey]) && self.filters[propKey].length > 0) {
            this.gameParameters.push(name);
          }
        }

        this.actualizeProviders();
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting games recommendations');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setFiltersLoading(false);
      }

      return this.filters;
    },

    async listCollectionPresets(query?: string): Promise<CollectionPresetResponse[]> {
      this.setPresetLoading(true);

      this.presets = [];

      try {
        const data = await client.collectionsApi.listCollectionPresets(this.collectionSourceId, {
          query: query || this.presetSearch || undefined,
        });

        this.presetTotal = data.total ?? 0;
        this.presets = data.items ?? [];
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting games recommendations');

        ElMessage.error(message);
      } finally {
        this.setPresetLoading(false);
      }

      return this.presets;
    },

    async getAvailableCountries(): Promise<ZingBrainAvailableCountries> {
      this.setError(null);
      this.setAvailableCountriesLoading(true);

      this.availableCountries = {
        segmentType: 'country',
        values: [],
      };

      try {
        this.availableCountries = await client.collectionsApi.getAvailableCountries();
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting available countries');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setAvailableCountriesLoading(false);
      }

      return this.availableCountries;
    },

    async createCollectionPreset(name: string): Promise<void> {
      try {
        await client.collectionsApi.createCollectionPreset({
          collectionSourceId: this.collectionSourceId,
          name,
          parameters: this.gameRecommendationsRequest ?? {},
        });

        ElMessage.success(`Preset "${name}" was created successfully`);

        await this.listCollectionPresets();
      } catch (err) {
        if ((err as Error).message === 'Server error') {
          throw new Error('Name must be unique');
        }

        const message = getErrorMessage(err, 'Error saving preset');
        ElMessage.error(message);
      }
    },
  },
});
